/**
 * Toggle Config
 *
 * Toggle wird nur bei Multiform verwendet und hier initialisiert
 */
interface ProcessFormType {
    languageKey: string | null;
    loaded: boolean;
    toggleItem: HTMLElement | null;
    index: number | null;
    formContainerElem: HTMLDivElement | null;
    badgeElem: HTMLElement | null;
}

interface SearchboxOptionType {
    skipCookieConsent?: boolean;
}

if (window.TCMS.searchbox.isToggleInitialized === false) {
    /**
     * Class TCMS Searchbox Toggle
     */
    class TcmsSearchboxToggle {

        /**
         *
         * @param {string} device
         * @param {string} lang
         * @param {object} options
         */

        skipCookieConsent: boolean;

        device: string;

        language: string;

        searchBoxContent: HTMLDivElement | null;

        toggleContainer?: HTMLDivElement | null;

        toggleElements?: NodeListOf<Element> | null;

        badges?: NodeListOf<HTMLElement> | null;

        forms?: NodeListOf<HTMLDivElement> | null;

        formsPlaceholder?: NodeListOf<HTMLElement> | null;

        formsStorage?: Record<
            string,
            {
                badge: HTMLElement | null;
                form: HTMLDivElement;
                loaded: boolean;
                toggle: HTMLElement | null;
            }
        >;

        constructor(device: string, lang: string, options: SearchboxOptionType) {
            this.skipCookieConsent = !!options.skipCookieConsent || false; // Set this to false to load the first form
            // immediately
            this.device = device;
            this.language = lang;
            this.searchBoxContent = document.querySelector<HTMLDivElement>('.ck24_searchbox_wdgt .sb-content');

            if (this.searchBoxContent) {
                this.toggleContainer = this.searchBoxContent.querySelector<HTMLDivElement>('.sb-toggle');
                this.toggleElements = null;
                this.badges = null;
                this.forms = null;
                this.formsPlaceholder = null;

                this.formsStorage = {};

                this.init(); // Initialize the Toggleator
            }
        }

        /**
         * @description Initializes the elements in the main container of toggle
         */
        init(): void {
            this.forms = this.searchBoxContent?.querySelectorAll('.sb-forms .formItem');
            this.badges = this.searchBoxContent?.querySelectorAll('.sb-badges .badgeItem');

            if (this.toggleContainer) {
                this.toggleElements = this.toggleContainer.querySelectorAll('.toggleItem');
            }

            if (this.forms && this.badges) {
                this.processForms();
            }
        }

        /**
         * @description Processes all the form elements found in the main container and builds up the
         *              tabs and contents
         */
        processForms(): void {
            if (!this.forms || !this.badges) return;
            const formsArray = Array.from(this.forms);
            const badgesArray = Array.from(this.badges);

            if (this.toggleElements && this.toggleElements?.length > 0) {
                this.toggleElements.forEach((toggleItem, index) => {
                    if (toggleItem instanceof HTMLElement) {
                        const languageKey = toggleItem.dataset.languagekey; // Get data-key e.g. 'hotel.de', 'urlaub.de'
                        //                    const language          = this.language
                        //                    const languageKey       = `${key}.${language}`;
                        const loaded = toggleItem.dataset.loaded === 'true';
                        const formContainerElem = formsArray.find(({ dataset }) => dataset.languagekey === languageKey);
                        const badgeElem = badgesArray.find(({ dataset }) => dataset.languagekey === languageKey);

                        if (!languageKey || !formContainerElem || !badgeElem) return;
                        this.processForm({
                            languageKey,
                            loaded,
                            toggleItem,
                            index,
                            formContainerElem,
                            badgeElem
                        });
                    }
                });
            } else {
                // Single Form
                const formContainerElem = formsArray.length > 0 ? formsArray[0] : null;
                const badgeElem = badgesArray.length > 0 ? badgesArray[0] : null;

                if (formContainerElem) {
                    const languageKey = formContainerElem.dataset.languagekey; // Get data-key e.g. 'hotel.de',
                    // 'urlaub.de'
                    const loaded = false;
                    const toggleItem = null;
                    const index = null;

                    if (!languageKey) return;
                    this.processForm({
                        languageKey,
                        loaded,
                        toggleItem,
                        index,
                        formContainerElem,
                        badgeElem
                    });
                }
            }
        }

        processForm({
            languageKey = null,
            loaded = false,
            toggleItem = null,
            index = null,
            formContainerElem = null,
            badgeElem = null
        }: ProcessFormType): void {
            if (languageKey !== null && formContainerElem !== null) {
                if (this.formsStorage) {
                    this.formsStorage[languageKey] = {
                        toggle: toggleItem,
                        badge: badgeElem,
                        form: formContainerElem,
                        loaded: loaded
                    };

                    // Add switch event if toggle is available
                    if (toggleItem !== null) {
                        this.addSwitchEvent(languageKey);
                    }

                    // If Toggle exists and it is item with index 0 or if there is no toggle -> Switch to form
                    if (index === 0 || toggleItem === null) {
                        this.switchFormTo(languageKey);
                    }
                }
            }
        }

        /**
         * @description Add the event to switch to any form included in the main container
         *
         * @param {string} key Name or ID of the form which should be switched to
         */
        addSwitchEvent(key: string): void {
            if (this.formsStorage) {
                const formStoreObj = this.formsStorage[key];

                if (formStoreObj) {
                    formStoreObj.toggle?.addEventListener('click', (e) => {
                        if (e.currentTarget instanceof HTMLElement) {
                            const target = e.currentTarget;

                            e.stopPropagation();

                            if (!target.classList.contains('sb-toggle-active')) {
                                const _key = target.dataset.languagekey;

                                if (_key) {
                                    this.switchFormTo(_key);
                                }
                            }
                        }
                    });
                }
            }
        }

        /**
         * @description Switches the displayed form to the given key
         * @param {string} key Name or ID of the form which should be switched to
         */
        switchFormTo(key: string): void {
            this.resetAllActiveStates(); // Reset classes for active state of toggle
            this.setLoadingStatus(key);

            const loadFormAndActivate = (key: string) => {
                const assetsLoadedProm = this.loadFormAssets(key);

                this.setLoadedStatus(key, assetsLoadedProm);
            };

            if (this.skipCookieConsent === false && window.TCMS.helper.addConsentCookieEvent) {
                window.TCMS.helper.addConsentCookieEvent('f', () => {
                    loadFormAndActivate(key);
                });
            } else {
                loadFormAndActivate(key);
            }
        }

        /**
         * @description Reset toggle active class of all toggle Items
         */
        resetAllActiveStates(): void {
            if (this.formsStorage) {
                for (const [, formObj] of Object.entries(this.formsStorage)) {
                    if (formObj.toggle) {
                        formObj.toggle.classList.remove('sb-toggle-active');
                    }

                    if (formObj.badge) {
                        formObj.badge.classList.remove('sb-show');
                    }

                    if (formObj.form) {
                        formObj.form.classList.remove('sb-show');
                    }
                }
            }
        }

        /**
         * @description Adds the active class to the selected form elements to show the placeholder
         * @param {string} key
         */
        setLoadingStatus(key: string): void {
            if (this.formsStorage) {
                const formStoreObj = this.formsStorage[key];

                if (formStoreObj) {
                    if (formStoreObj.toggle) {
                        formStoreObj.toggle.classList.add('sb-toggle-active');
                    }

                    if (formStoreObj.form) {
                        formStoreObj.form.classList.add('sb-show'); // Will be done by loadFormAssets import script the css
                    }
                }
            }
        }

        /**
         * @description Adds the active class to the selected form elements
         * @param {string} key
         * @param {Promise} assetsLoadedProm
         */
        setLoadedStatus(key: string, assetsLoadedProm: Promise<void>): void {
            if (this.formsStorage) {
                const formStoreObj = this.formsStorage[key];

                assetsLoadedProm.then(() => {
                    if (formStoreObj.badge) {
                        formStoreObj.badge.classList.add('sb-show');
                    }

                    if (formStoreObj.form) {
                        const formElem = formStoreObj.form.querySelector('.formContent');
                        const placeholder = formStoreObj.form.querySelector('.formItemPlaceholder');

                        if (placeholder instanceof HTMLElement && formElem) {
                            placeholder.style.display = 'none';
                            formElem.classList.add('sb-show');
                        }
                    }
                });
            }
        }

        /**
         * @description Loads assets of any form by dynamic import
         * @param {string} key
         * @return {*}
         */
        loadFormAssets(key: string): Promise<void> {
            return new Promise<void>((resolve) => {
                if (this.formsStorage) {
                    if (this.formsStorage[key].loaded !== true) {
                        import(`../../searchbox_imports/import.${key}.js`)
                            .then(() => {
                                if (this.formsStorage) {
                                    this.formsStorage[key].form.dataset.loaded = 'true';
                                    this.formsStorage[key].loaded = true;
                                    resolve();
                                }
                            })
                            .catch((err) => {
                                console.error(new Error('Form asset could not be loaded.'));
                                console.error(err);
                            });
                    } else {
                        resolve();
                    }
                }
            });
        }

    }

    window.TCMS.searchbox.toggleInstance = new TcmsSearchboxToggle(
        window.TCMS.viewtype,
        window.TCMS.language,
        window.TCMS.searchbox
    );
    window.TCMS.searchbox.isToggleInitialized = true;
}
